import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UnderlineTabs } from "@cohesive/ui-components";
import { MoreHorizontal, Search, X } from "lucide-react";
import { ViewModeToggle } from "../../components/ViewModeToggle";
import {
  naturalHomeSort,
  type HomeSort,
  type HomeSortField,
  type HomeTab,
  type HomeViewMode,
} from "../utils/homeBrowserTypes";

/** Home strip. No mixed view — a merged recents grid was where container
 *  files looked free-floating. Favorites carries canvases + Library files
 *  only (chats pin instead; skills and connectors sit this one out). The
 *  "recents" id stays (storage keys / empty copy) — the label is Files. */
const TABS: { id: HomeTab; label: string }[] = [
  { id: "canvases", label: "Canvases" },
  { id: "recents", label: "Files" },
  { id: "chats", label: "Chats" },
  { id: "starred", label: "Favorites" },
  { id: "shared", label: "Shared with me" },
];

/** Detail-page strip: the home order minus Shared — one order everywhere. */
export const WORKSPACE_BROWSER_TABS: { id: HomeTab; label: string }[] = [
  { id: "canvases", label: "Canvases" },
  { id: "recents", label: "Files" },
  { id: "chats", label: "Chats" },
  { id: "starred", label: "Favorites" },
];

/** Field picker for the view-options kebab — mostly for grid view, which has
 *  no column headers. Picking a field snaps to its natural default direction;
 *  the list view's column headers are where direction gets toggled. */
const SORT_OPTIONS: { value: HomeSortField; label: string }[] = [
  { value: "modified", label: "Last updated" },
  { value: "name", label: "Name" },
];

interface HomeBrowserHeaderProps {
  tab: HomeTab;
  onTabChange: (tab: HomeTab) => void;
  view: HomeViewMode;
  onViewChange: (view: HomeViewMode) => void;
  sort: HomeSort;
  onSortChange: (sort: HomeSort) => void;
  newMenu: React.ReactNode;
  /** Omitted on unscoped home, which has no search affordance. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchLabel?: string;
  /** Tab strip override (defaults to the home labels). */
  tabs?: { id: HomeTab; label: string }[];
  /** Wide-layout toggle (full page width vs. the 58rem column). Omit to hide. */
  wide?: boolean;
  onWideChange?: (wide: boolean) => void;
}

/**
 * Tab strip (All · Files · Canvases · Chats · Starred) + search/view/sort/new
 * cluster. Search lives here rather than beside the page title so the composer
 * above keeps the same position on every surface.
 */
export function HomeBrowserHeader({
  tab,
  onTabChange,
  view,
  onViewChange,
  sort,
  onSortChange,
  newMenu,
  search,
  onSearchChange,
  searchLabel = "Search",
  tabs = TABS,
  wide = false,
  onWideChange,
}: HomeBrowserHeaderProps) {
  return (
    <UnderlineTabs
      variant="plain"
      tabs={tabs}
      value={tab}
      onValueChange={(id) => onTabChange(id as HomeTab)}
      rightSlot={
        <div className="flex items-center gap-2">
          {onSearchChange && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search ?? ""}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search..."
                aria-label={searchLabel}
                className="h-8 w-44 rounded-full bg-muted/50 pl-8 pr-8 text-sm select-text"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => onSearchChange("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <ViewModeToggle value={view} onChange={onViewChange} />
          {/* View-options kebab: sort + the full-width layout toggle. */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="View options"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>View options</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={sort.field}
                onValueChange={(field) =>
                  onSortChange(naturalHomeSort(field as HomeSortField))
                }
              >
                {SORT_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              {onWideChange && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={wide}
                    onCheckedChange={(next) => onWideChange(!!next)}
                  >
                    Full width
                  </DropdownMenuCheckboxItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {newMenu}
        </div>
      }
    />
  );
}
